
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buscarSolicitacaoReservaPorTokenService,
  buscarResumoEstadiaByTokenService,
  buscarPagamentoTaxaPorTokenService,
  reenviarPagamentoTaxaPorTokenService,
  upsertHospedesSolicitacaoReservaPorTokenService,
  atualizarContatoSolicitacaoReservaPorTokenService,
  atualizarStatusSolicitacaoReservaPorTokenService,
  type PeriodoProcuradoPublico,
  type PagamentoTaxaCheckoutStatus,
  type ResumoEstadiaByTokenResponse,
  type SolicitacaoReservaFlowError,
  buscarHospedesSolicitacaoReservaPorTokenService,
  salvarFormaPagamentoPorTokenService,
  salvarAceiteTermosPorTokenService,
} from "../../services/solicitacaoReservaService";
import SolicitacaoReservaStatusTag, {
  type StatusSolicitacaoReservaFront,
} from "./components/SolicitacaoReservaStatusTag";
import StepTaxaTermosPagamento from "./Steps/StepTaxaTermosPagamento";
import StepTaxaPagaAcompanhamento from "./Steps/StepTaxaPagaAcompanhamento";
import StepAcompanhamentoSolicitacao from "./Steps/StepAcompanhamentoSolicitacao";
import StepAguardandoPagamentoTaxa from "./Steps/StepAguardandoPagamentoTaxa";
import StepSemInteressePeriodo from "./Steps/StepSemInteressePeriodo";
import StepAgradecimentoSemInteresse from "./Steps/StepAgradecimentoSemInteresse";
import StepEstadoSolicitacao from "./Steps/StepEstadoSolicitacao";
import { formatDate } from "../../uteis/dateUtils";

import { HomeOutlined, CalendarOutlined, InfoCircleOutlined, CoffeeOutlined } from "@ant-design/icons";
import {
  Alert,
  Skeleton,
  Tag,
  Button,
  Card,
  Divider,
  Col,
  Drawer,
  Grid,
  Progress,
  Row,
  Space,
  Steps,
  Typography,
  message,
  Modal,
} from "antd";
import StepIdentificacao, { IdentificacaoValues } from "./Steps/StepIdentificacao";
import { iniciarSolicitacaoReservaService } from "../../services/solicitacaoReservaService";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import StepHospedes from "./Steps/StepHospedes";

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const moeda = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const SOLICITACAO_RESERVA_INVALIDA_MSG =
  "Essa solicitação de reserva não está mais válida. Qualquer dúvida entre em contato.";

const PAGAMENTO_TAXA_INTERROMPIDO_MSG =
  "Não podemos te atender agora, mas estamos procurando outro período para você.";

type SolicitacaoReservaSpecialState =
  | {
      kind: "solicitacao_invalida";
      message: string;
    }
  | {
      kind: "pagamento_interrompido";
      message: string;
    }
  | null;

type CheckoutReturnStatus = "success" | "cancel" | "expired" | null;

type StepKey =
  | "identificacao"
  | "revisao"
  | "proposta_pagamento"
  | "processo_prazos"
  | "dados_hospedes"
  | "taxa_termos_pagamento";

const stepDefs: { key: StepKey; title: string; short: string }[] = [
  { key: "identificacao", title: "Identificação", short: "Dados" },
  { key: "revisao", title: "Revisão da estadia", short: "Estadia" },
  { key: "proposta_pagamento", title: "Proposta e pagamento", short: "Proposta" },
  { key: "processo_prazos", title: "Processo e prazos", short: "Prazos" },
  { key: "dados_hospedes", title: "Dados dos hóspedes", short: "Hóspedes" },
  { key: "taxa_termos_pagamento", title: "Taxa e termos", short: "Taxa" },
];

const pickFirstDate = (...values: Array<string | null | undefined>) =>
  values.find((value) => typeof value === "string" && value.trim().length > 0) ?? null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getObjectDate = (
  source: Record<string, unknown> | null | undefined,
  keys: string[]
) =>
  pickFirstDate(
    ...keys.map((key) =>
      typeof source?.[key] === "string" ? (source[key] as string) : null
    )
  );

const normalizeDateSearchKey = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const findDeepDateByTokens = (
  source: unknown,
  requiredTokens: string[],
  dateTokens: string[],
  trail: string[] = []
): string | null => {
  if (typeof source === "string") {
    const normalizedTrail = normalizeDateSearchKey(trail.join(" "));
    const hasRequiredTokens = requiredTokens.every((token) =>
      normalizedTrail.includes(token)
    );
    const hasDateToken = dateTokens.some((token) =>
      normalizedTrail.includes(token)
    );

    return hasRequiredTokens && hasDateToken ? source : null;
  }

  if (Array.isArray(source)) {
    for (let index = 0; index < source.length; index += 1) {
      const match = findDeepDateByTokens(source[index], requiredTokens, dateTokens, [
        ...trail,
        String(index),
      ]);
      if (match) return match;
    }
    return null;
  }

  if (!isRecord(source)) return null;

  for (const [key, value] of Object.entries(source)) {
    const match = findDeepDateByTokens(value, requiredTokens, dateTokens, [
      ...trail,
      key,
    ]);
    if (match) return match;
  }

  return null;
};

const formatPeriodoResumo = (
  inicio?: string | null,
  fim?: string | null,
  separator = "→"
) => {
  if (inicio && fim) return `${formatDate(inicio)} ${separator} ${formatDate(fim)}`;
  if (inicio) return formatDate(inicio);
  if (fim) return formatDate(fim);
  return "—";
};

const getPeriodoDate = (
  periodo: PeriodoProcuradoPublico | null | undefined,
  edge: "inicio" | "fim"
) =>
  edge === "inicio"
    ? pickFirstDate(
        periodo?.dataIni,
        periodo?.periodoProcuradoIni,
        periodo?.checkinProcurado,
        periodo?.checkin,
        periodo?.inicio
      )
    : pickFirstDate(
        periodo?.dataFim,
        periodo?.periodoProcuradoFim,
        periodo?.checkoutProcurado,
        periodo?.checkout,
        periodo?.fim
      );

const resolvePeriodoAprovadoDate = (
  resumo: ResumoEstadiaByTokenResponse | null,
  edge: "inicio" | "fim"
) => {
  if (!resumo) return null;

  const periodoEncontrado = resumo.periodoEncontrado;
  const resolvedFromContract =
    edge === "inicio" ? periodoEncontrado?.checkin : periodoEncontrado?.checkout;
  if (resolvedFromContract) return resolvedFromContract;

  const raw = resumo as unknown as Record<string, unknown>;
  const nestedSources = [
    "origemDisp",
    "origemDisponibilidade",
    "origemDisponivel",
    "origemDisponibilidadePeriodo",
    "periodoEncontrado",
    "periodoAprovado",
    "periodoDisponivel",
    "periodoSelecionado",
    "periodoReserva",
    "periodo",
    "estadia",
    "periodoUsuario",
  ]
    .map((key) => raw[key])
    .filter(isRecord);

  const explicitKeys =
    edge === "inicio"
      ? [
          "checkinOrigemDisp",
          "checkinOrigemDisponibilidade",
          "dataIniOrigemDisp",
          "dataInicioOrigemDisp",
          "inicioOrigemDisp",
          "checkinEncontrado",
          "checkinAprovado",
          "dataIniEncontrado",
          "dataInicioEncontrado",
          "inicioEncontrado",
          "inicioAprovado",
        ]
      : [
          "checkoutOrigemDisp",
          "checkoutOrigemDisponibilidade",
          "dataFimOrigemDisp",
          "dataFinalOrigemDisp",
          "fimOrigemDisp",
          "checkoutEncontrado",
          "checkoutAprovado",
          "dataFimEncontrado",
          "dataFinalEncontrado",
          "fimEncontrado",
          "fimAprovado",
        ];

  const fallbackKeys =
    edge === "inicio"
      ? ["checkin", "inicio", "dataIni", "dataInicio"]
      : ["checkout", "fim", "dataFim", "dataFinal"];

  const originDateTokens =
    edge === "inicio"
      ? ["checkin", "inicio", "entrada", "dataini", "datainicio"]
      : ["checkout", "fim", "saida", "datafim", "datafinal"];

  return pickFirstDate(
    findDeepDateByTokens(raw, ["origem", "disp"], originDateTokens),
    findDeepDateByTokens(raw, ["origem", "dispon"], originDateTokens),
    findDeepDateByTokens(raw, ["origem"], originDateTokens),
    ...nestedSources.map((source) => getObjectDate(source, explicitKeys)),
    getObjectDate(raw, explicitKeys),
    ...nestedSources.map((source) => getObjectDate(source, fallbackKeys)),
    getObjectDate(raw, fallbackKeys)
  );
};

const resolveNoitesAprovadas = (resumo: ResumoEstadiaByTokenResponse | null) =>
  resumo?.periodoEncontrado?.noites ?? resumo?.noites ?? null;

const normalizeStatusSolicitacao = (
  status: string | null | undefined
): StatusSolicitacaoReservaFront | null => {
  if (!status) return null;
  if (status === "SR_ENVIADA") return "INICIADA";
  return status as StatusSolicitacaoReservaFront;
};

const getSolicitacaoReservaSpecialState = (
  error: SolicitacaoReservaFlowError | null
): SolicitacaoReservaSpecialState => {
  if (!error) return null;

  if (error.code === "SOLICITACAO_RESERVA_INVALIDA") {
    return {
      kind: "solicitacao_invalida",
      message: error.message || SOLICITACAO_RESERVA_INVALIDA_MSG,
    };
  }

  if (
    error.code === "PAGAMENTO_TAXA_INTERROMPIDO" ||
    error.statusSolicitacaoReserva === "PAGAMENTO_TX_INTERROMPIDO"
  ) {
    return {
      kind: "pagamento_interrompido",
      message: error.message || PAGAMENTO_TAXA_INTERROMPIDO_MSG,
    };
  }

  return null;
};

const normalizeCheckoutReturnStatus = (
  value: string | null | undefined
): CheckoutReturnStatus => {
  if (value === "success" || value === "cancel" || value === "expired") {
    return value;
  }

  return null;
};

const getPagamentoTaxaCheckoutLink = (payload: any): string | null => {
  if (!payload || typeof payload !== "object") return null;

  const nestedLink = payload.pagamentoTaxa?.linkPagamento;
  if (typeof nestedLink === "string" && nestedLink.trim().length > 0) {
    return nestedLink;
  }

  if (typeof payload.linkPagamento === "string" && payload.linkPagamento.trim().length > 0) {
    return payload.linkPagamento;
  }

  if (
    typeof payload.linkPagamentoTaxa === "string" &&
    payload.linkPagamentoTaxa.trim().length > 0
  ) {
    return payload.linkPagamentoTaxa;
  }

  return null;
};

const getPagamentoTaxaCheckoutStatus = (
  payload: any
): PagamentoTaxaCheckoutStatus | null => {
  if (!payload || typeof payload !== "object") return null;

  const status = payload.pagamentoTaxa?.status ?? payload.status ?? null;

  if (
    status === "PENDENTE" ||
    status === "RECEBIDO" ||
    status === "VENCIDO" ||
    status === "CANCELADO"
  ) {
    return status;
  }

  return null;
};

function ResumoCard({
  currentStep,
  currentStepKey,
  resumo,
  periodoAprovadoIni,
  periodoAprovadoFim,
  formaPagamento,
  statusSolicitacao,
    forceShowPrice = false,
}: {
  currentStep: number;
  currentStepKey?: StepKey | null;
  resumo: ResumoEstadiaByTokenResponse | null;
  periodoAprovadoIni?: string | null;
  periodoAprovadoFim?: string | null;
  formaPagamento: "PIX" | "CARTAO" | null;
  statusSolicitacao: StatusSolicitacaoReservaFront | null;
  forceShowPrice?: boolean;
}) {
  // stepDefs: 0 ident, 1 revisao, 2 proposta_pagamento
  const showPrice = forceShowPrice || currentStep >= 2;
  const showVerificationFee =
    forceShowPrice ||
    (currentStepKey !== "identificacao" && currentStepKey !== "revisao");

  const resort = resumo?.empreendimento?.nome ?? "—";
  const datas = resumo ? formatPeriodoResumo(periodoAprovadoIni, periodoAprovadoFim) : "—";
  const noites = resolveNoitesAprovadas(resumo);

  const totalSelecionado =
    !resumo
      ? null
      : formaPagamento === "PIX"
      ? resumo.pix.totalRestante
      : formaPagamento === "CARTAO"
      ? resumo.cartao.totalRestanteFinal
      : null;

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 16,
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
      }}
      styles={{ body: { padding: 20 } }}
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <div style={{ marginBottom: 10 }}>
          <SolicitacaoReservaStatusTag status={statusSolicitacao} />
        </div>
        <Title level={4} style={{ margin: 0 }}>
          Resumo
        </Title>

        <Divider style={{ margin: "6px 0" }} />

        {/* Resort */}
        <div>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Resort
          </Text>
          <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.2 }}>
            {resort}
          </div>
        </div>

        {/* Datas */}
        <div>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Datas
          </Text>
          <div style={{ fontSize: 15, fontWeight: 650 }}>{datas}</div>
          {noites !== null && (
            <Tag style={{ marginTop: 8, borderRadius: 999, padding: "2px 10px" }}>
              {noites} noites
            </Tag>
          )}
        </div>

        {/* Valor só após Step proposta_pagamento */}
        {showPrice && (
  <>
    <Divider style={{ margin: "6px 0" }} />

    {/* Forma de pagamento */}
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <Text type="secondary" style={{ fontSize: 13 }}>
        Forma de pagamento
      </Text>
      <Text style={{ fontSize: 13, fontWeight: 700 }}>
        {formaPagamento === "CARTAO" ? "Cartão" : formaPagamento === "PIX" ? "PIX" : "—"}
      </Text>
    </div>

    {(() => {
      if (!resumo) return null;

      const taxa = resumo.taxaCondicional ?? 0;

      // "Restante" depende da forma selecionada
      const restante =
        formaPagamento === "PIX"
          ? resumo.pix.totalRestante
          : formaPagamento === "CARTAO"
          ? resumo.cartao.totalRestanteFinal
          : null;

      // Total da proposta (hospedagem) = restante + taxa
      const totalProposta = restante !== null ? restante + taxa : null;

      // Economia no PIX vs cartão (opcional)
      const economiaPix =
        resumo.cartao.totalRestanteFinal > resumo.pix.totalRestante
          ? resumo.cartao.totalRestanteFinal - resumo.pix.totalRestante
          : 0;

      const RowItem = ({
        label,
        value,
        secondary,
        strong,
      }: {
        label: React.ReactNode;
        value: React.ReactNode;
        secondary?: boolean;
        strong?: boolean;
      }) => (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            marginTop: 6,
          }}
        >
          <Text type={secondary ? "secondary" : undefined} style={{ fontSize: 13 }}>
            {label}
          </Text>
          <Text strong={strong} style={{ fontSize: 13 }}>
            {value}
          </Text>
        </div>
      );

      return (
        <>
          <div style={{ marginTop: 6 }}>
            <RowItem
              label="Valor total da proposta"
              value={totalProposta !== null ? moeda(totalProposta) : "—"}
              strong
            />

            {showVerificationFee && (
              <RowItem
              label="Taxa de verificação"
              value={taxa ? `- ${moeda(taxa)}` : "—"}
              secondary
              />
            )}

            <Divider style={{ margin: "10px 0" }} />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
              }}
            >
              <Text style={{ fontSize: 15, fontWeight: 800 }}>
                Saldo restante a pagar
              </Text>

             <Text
                style={{
                  fontSize: 20,
                  fontWeight: 900,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {restante !== null ? moeda(restante) : "—"}
              </Text>
            </div>

            {/* Detalhes por forma */}
           {formaPagamento === "CARTAO" && (
              <>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Parcelamento disponível em até {resumo.cartao.parcelas}x.<br />
                  Exemplo: {resumo.cartao.parcelas}x de {moeda(resumo.cartao.valorParcelaRestante)}.
                </Text>
                {resumo.cartao.parcelas > 12 && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="warning" style={{ fontSize: 13 }}>
                      Parcelamentos acima de 12x são permitidos somente para cartões Visa e Mastercard.
                    </Text>
                  </div>
                )}
              </>
            )}

            {formaPagamento === "PIX" && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                Economia no PIX: {moeda(economiaPix)}
              </Text>
            )}
            <br/><br/><Text type="secondary" style={{ fontSize: 12 }}>
  A forma de pagamento final e o número de parcelas poderão ser alterados
  no momento do pagamento do saldo restante.
</Text>
          </div>
        </>
      );
    })()}
  </>
)}

        {/* Mensagem contextual (opcional, mas ajuda muito) */}
        {!showPrice ? (
          <Alert
            type="info"
            showIcon
            message="Confira as etapas"
            description="Você verá a proposta e as condições nas próximas etapas."
            style={{ borderRadius: 14 }}
          />
        ) : (
          <></>
      //     <Alert
      //   type="info"
      //   showIcon
      //   message="Valores sujeitos à confirmação"
      //   description="Se houver mudança de valor ou indisponibilidade, avisaremos antes. Se não quiser seguir, não paga o valor restante e a taxa é devolvida."
      //   style={{ borderRadius: 14 }}
      // />
        )}
      </Space>
    </Card>
  );
}

export default function ViewSolicitacaoReserva() {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const topAnchorRef = useRef<HTMLDivElement | null>(null);

  const [current, setCurrent] = useState(0);
  const [openResumo, setOpenResumo] = useState(false);

  const navigate = useNavigate();
  const { token: routeToken } = useParams(); // rota /solicitacao-reserva/:token
  const [searchParams] = useSearchParams();
  const checkoutSrToken = searchParams.get("srToken");
  const checkoutStatusParam = normalizeCheckoutReturnStatus(
    searchParams.get("checkoutStatus")
  );
  const token = routeToken ?? checkoutSrToken ?? undefined;

  const [loadingToken, setLoadingToken] = useState(false);
const [solicitacaoReservaId, setSolicitacaoReservaId] = useState<number | null>(null);
const [initialIdentData, setInitialIdentData] = useState<Partial<IdentificacaoValues> | null>(null);

  const [loadingIdent, setLoadingIdent] = useState(false);

  const loadingStep1 = loadingIdent || loadingToken;
  const [loadingResumo, setLoadingResumo] = useState(false);
  const [resumo, setResumo] = useState<ResumoEstadiaByTokenResponse | null>(null);
  const [formaPagamento, setFormaPagamento] = useState<"PIX" | "CARTAO" | null>(null);
  const [formaPagamentoInicial, setFormaPagamentoInicial] = useState<"PIX" | "CARTAO" | null>(null);
   const [modalOpen, setModalOpen] = useState(false);  
   const valorTaxa = resumo?.taxaCondicional ?? 0;
   const [loadingHospedes, setLoadingHospedes] = useState(false);
const [hospedesInitial, setHospedesInitial] = useState<any>(null);
const [statusSolicitacao, setStatusSolicitacao] =
  useState<StatusSolicitacaoReservaFront | null>(null);
const [loadingTermos, setLoadingTermos] = useState(false);
const [loadingPagamentoTaxa, setLoadingPagamentoTaxa] = useState(false);
const [pagamentoTaxaEm, setPagamentoTaxaEm] = useState<string | null>(null);
const [pagamentoTaxaStatus, setPagamentoTaxaStatus] =
  useState<PagamentoTaxaCheckoutStatus | null>(null);
const [linkPagamentoTaxaCheckout, setLinkPagamentoTaxaCheckout] =
  useState<string | null>(null);
const [prazoFinalBusca, setPrazoFinalBusca] = useState<string | null>(null);
const [periodoProcuradoIni, setPeriodoProcuradoIni] = useState<string | null>(null);
const [periodoProcuradoFim, setPeriodoProcuradoFim] = useState<string | null>(null);
const [loadingSemInteresse, setLoadingSemInteresse] = useState(false);
const [specialState, setSpecialState] =
  useState<SolicitacaoReservaSpecialState>(null);


  const stepItems = useMemo(
    () =>
      stepDefs.map((s) => ({
        title: isMobile ? s.short : s.title,
      })),
    [isMobile]
  );

const acao = searchParams.get("acao");
const isSemInteresseMode = !!token && acao === "sem-interesse";
const periodoProcuradoIniParam = pickFirstDate(
  searchParams.get("periodoProcuradoIni"),
  searchParams.get("dataIni"),
  searchParams.get("checkinProcurado"),
  searchParams.get("checkin")
);
const periodoProcuradoFimParam = pickFirstDate(
  searchParams.get("periodoProcuradoFim"),
  searchParams.get("dataFim"),
  searchParams.get("checkoutProcurado"),
  searchParams.get("checkout")
);

 const isWizardMode =
  statusSolicitacao === "CRIADA" ||
  statusSolicitacao === "INICIADA" ||
  !statusSolicitacao;

const isAguardandoPagamentoTaxa =
  statusSolicitacao === "AGUARDANDO_PAGAMENTO_TAXA";

const isAcompanhamentoTaxaPaga =
  statusSolicitacao === "TAXA_PAGA";

const isPagamentoTaxaInterrompido =
  statusSolicitacao === "PAGAMENTO_TX_INTERROMPIDO";

const isAcompanhamentoPosTaxa =
  statusSolicitacao === "EM_VERIFICACAO" ||
  statusSolicitacao === "RESERVA_CONFIRMADA" ||
  statusSolicitacao === "RESERVA_FINALIZADA" ||
  statusSolicitacao === "RESERVA_CANCELADA";

const isSemInteresseConcluido =
  statusSolicitacao === "SEM_INTERESSE_PERIODO_PROCURADO" ||
  statusSolicitacao === "SEM_INTERESSE_PERIODO_ENCONTRADO";

const resolvedSpecialState = useMemo(
  () =>
    specialState ??
    (isPagamentoTaxaInterrompido
      ? {
          kind: "pagamento_interrompido" as const,
          message: PAGAMENTO_TAXA_INTERROMPIDO_MSG,
        }
      : null),
  [isPagamentoTaxaInterrompido, specialState]
);

  const pct = Math.round(((current + 1) / stepDefs.length) * 100);

  const next = () => setCurrent((c) => Math.min(c + 1, stepDefs.length - 1));
  const back = () => setCurrent((c) => Math.max(c - 1, 0));

  useEffect(() => {
    if (typeof window === "undefined") return;

    const scrollToTop = () => {
      if (topAnchorRef.current) {
        topAnchorRef.current.scrollIntoView({ block: "start", behavior: "auto" });
        return;
      }

      window.scrollTo({ top: 0, behavior: "auto" });
    };

    const rafId = window.requestAnimationFrame(scrollToTop);
    return () => window.cancelAnimationFrame(rafId);
  }, [current, statusSolicitacao, resolvedSpecialState?.kind, isSemInteresseMode]);

  // ⚠️ TODO: substituir por dados reais vindos da listagem (querystring ou state)
  const context = {
    empreendId: 13,
    dataIni: "2026-04-12",
    dataFim: "2026-04-19",
    precoBaseProprietario: 2000,
    valorPropostoClienteInicial: 2000,
    aluguel: 1,
    troca: 0,
    compra: 0,
    venda: 0,
    validadeDias: 7,
  };

  const periodoAprovadoIni = useMemo(
    () => resolvePeriodoAprovadoDate(resumo, "inicio"),
    [resumo]
  );
  const periodoAprovadoFim = useMemo(
    () => resolvePeriodoAprovadoDate(resumo, "fim"),
    [resumo]
  );

  //Carregar hóspedes quando entrar no step dados_hospedes

  useEffect(() => {
  let cancelled = false;

  const loadHospedes = async () => {
    if (!token) return;

    const stepKey = stepDefs[current]?.key;
    if (stepKey !== "dados_hospedes") return;

    setLoadingHospedes(true);
    try {
      const resp = await buscarHospedesSolicitacaoReservaPorTokenService(token);
      if (!cancelled) setHospedesInitial(resp); // resp tem { hospedes: [...] }
    } finally {
      if (!cancelled) setLoadingHospedes(false);
    }
  };

  loadHospedes();
  return () => { cancelled = true; };
}, [token, current]);

// Carregar dados da SR quando tiver token (identificação)

useEffect(() => {
  let cancelled = false;

  const load = async () => {
    if (!token) {
      setSolicitacaoReservaId(null);
      setInitialIdentData(null);
      setSpecialState(null);
      setPagamentoTaxaEm(null);
      setPagamentoTaxaStatus(null);
      setLinkPagamentoTaxaCheckout(null);
      setPrazoFinalBusca(null);
      setPeriodoProcuradoIni(null);
      setPeriodoProcuradoFim(null);
      return;
    }

    setLoadingToken(true);

    try {
      console.log("[SR] carregando por token:", token);

      const resp = await buscarSolicitacaoReservaPorTokenService(token);

      if (cancelled) return;

      if (!resp.ok) {
        const nextSpecialState = getSolicitacaoReservaSpecialState(resp.error);

        if (nextSpecialState) {
          setSpecialState(nextSpecialState);
          setStatusSolicitacao(
            nextSpecialState.kind === "pagamento_interrompido"
              ? "PAGAMENTO_TX_INTERROMPIDO"
              : null
          );
          setResumo(null);
          setSolicitacaoReservaId(null);
          setInitialIdentData(null);
          setPagamentoTaxaEm(null);
          setPagamentoTaxaStatus(null);
          setLinkPagamentoTaxaCheckout(null);
          setPrazoFinalBusca(null);
          setPeriodoProcuradoIni(periodoProcuradoIniParam);
          setPeriodoProcuradoFim(periodoProcuradoFimParam);
          return;
        }
        console.warn("[SR] resposta inválida:", resp);
        message.error("Não foi possível carregar a solicitação (token inválido ou expirado).");
        setSolicitacaoReservaId(null);
        setInitialIdentData(null);
        setPagamentoTaxaEm(null);
        setPrazoFinalBusca(null);
        setPeriodoProcuradoIni(null);
        setPeriodoProcuradoFim(null);
        return;
      }

      if (!resp.data?.solicitacaoReserva?.id) {
        console.warn("[SR] resposta invÃ¡lida:", resp);
        message.error("NÃ£o foi possÃ­vel carregar a solicitaÃ§Ã£o (token invÃ¡lido ou expirado).");
        setSolicitacaoReservaId(null);
        setInitialIdentData(null);
        setPagamentoTaxaEm(null);
        setPagamentoTaxaStatus(null);
        setLinkPagamentoTaxaCheckout(null);
        setPrazoFinalBusca(null);
        setPeriodoProcuradoIni(null);
        setPeriodoProcuradoFim(null);
        return;
      }

      setSpecialState(null);

      const sr = resp.data.solicitacaoReserva;
      

      const fpRef = (sr.formaPagamentoPreferida as "PIX" | "CARTAO" | null) ?? null;
      const dataPagamento = pickFirstDate(
        sr.dataPagamentoTaxa,
        sr.pagamentoTaxaEm,
        sr.taxaPagaEm,
        sr.statusTaxaPagaEm
      );
      const prazoFinal = pickFirstDate(
        sr.prazoBuscaFinal,
        sr.prazoFinalBusca,
        sr.prazoAnaliseFinalEm,
        sr.prazoFinalAnaliseEm,
        sr.limiteAnaliseEm
      );
      const periodoProcuradoInicio = pickFirstDate(
        getPeriodoDate(resp.data.periodoProcurado, "inicio"),
        getPeriodoDate(resp.data.periodoProcura, "inicio"),
        getPeriodoDate(sr.periodoProcurado, "inicio"),
        getPeriodoDate(sr.periodoProcura, "inicio"),
        sr.dataIni,
        sr.periodoProcuradoIni,
        sr.checkinProcurado,
        periodoProcuradoIniParam
      );
      const periodoProcuradoFinal = pickFirstDate(
        getPeriodoDate(resp.data.periodoProcurado, "fim"),
        getPeriodoDate(resp.data.periodoProcura, "fim"),
        getPeriodoDate(sr.periodoProcurado, "fim"),
        getPeriodoDate(sr.periodoProcura, "fim"),
        sr.dataFim,
        sr.periodoProcuradoFim,
        sr.checkoutProcurado,
        periodoProcuradoFimParam
      );
      const precisaAtualizarStatus = !sr.status || sr.status === "CRIADA";
      const statusAtualizado = precisaAtualizarStatus
        ? await atualizarStatusSolicitacaoReservaPorTokenService(token, {
            status: "INICIADA",
          })
        : true;

      if (cancelled) return;

      if (precisaAtualizarStatus && !statusAtualizado) {
        console.warn("[SR] não foi possível atualizar status para INICIADA:", sr.id);
      }

      const statusCarregado =
        precisaAtualizarStatus && statusAtualizado
          ? "INICIADA"
          : normalizeStatusSolicitacao(sr.status) ?? "CRIADA";

      setFormaPagamento(fpRef);
      setFormaPagamentoInicial(fpRef);
      setStatusSolicitacao(statusCarregado);
      setPagamentoTaxaEm(dataPagamento);
      setPrazoFinalBusca(prazoFinal);
      setPeriodoProcuradoIni(periodoProcuradoInicio);
      setPeriodoProcuradoFim(periodoProcuradoFinal);

      setSolicitacaoReservaId(sr.id);
      setInitialIdentData({
        nome: sr.contatoNome,
        sobrenome: sr.contatoSobrenome,
        celular: sr.contatoCelular,
        email: sr.contatoEmail,
      });

      console.log("[SR] carregada:", sr.id);
    } catch (err) {
      if (cancelled) return;
      console.error("[SR] erro ao carregar:", err);
      message.error("Erro ao carregar a solicitação. Tente novamente.");
      setSolicitacaoReservaId(null);
      setInitialIdentData(null);
      setPagamentoTaxaEm(null);
      setPagamentoTaxaStatus(null);
      setLinkPagamentoTaxaCheckout(null);
      setPrazoFinalBusca(null);
      setPeriodoProcuradoIni(null);
      setPeriodoProcuradoFim(null);
    } finally {
      if (!cancelled) setLoadingToken(false);
    }
  };

  load();

  return () => {
    cancelled = true;
  };
}, [token, periodoProcuradoFimParam, periodoProcuradoIniParam, checkoutStatusParam]);

// Carregar resumo da estadia quando tiver token (step revisão)

useEffect(() => {
  let cancelled = false;

  const loadResumo = async () => {
    if (!token) {
      setResumo(null);
      return;
    }

    if (resolvedSpecialState?.kind === "solicitacao_invalida") {
      setResumo(null);
      return;
    }

    setLoadingResumo(true);
    try {
      const resp = await buscarResumoEstadiaByTokenService(token);
      if (cancelled) return;

      if (!resp.ok) {
        message.error("Não foi possível carregar o resumo da estadia.");
        setResumo(null);
        return;
      }

      if (!resp.data?.empreendimento?.id) {
        message.error("NÃ£o foi possÃ­vel carregar o resumo da estadia.");
        setResumo(null);
        return;
      }

      setResumo(resp.data);
      setLinkPagamentoTaxaCheckout(getPagamentoTaxaCheckoutLink(resp.data));
      setPagamentoTaxaStatus(getPagamentoTaxaCheckoutStatus(resp.data));
      setPeriodoProcuradoIni((prev) =>
        pickFirstDate(
          prev,
          getPeriodoDate(resp.data.periodoProcurado, "inicio"),
          getPeriodoDate(resp.data.periodoProcura, "inicio"),
          periodoProcuradoIniParam
        )
      );
      setPeriodoProcuradoFim((prev) =>
        pickFirstDate(
          prev,
          getPeriodoDate(resp.data.periodoProcurado, "fim"),
          getPeriodoDate(resp.data.periodoProcura, "fim"),
          periodoProcuradoFimParam
        )
      );
    } catch (e) {
      if (!cancelled) {
        console.error(e);
        message.error("Erro ao carregar resumo da estadia.");
        setResumo(null);
      }
    } finally {
      if (!cancelled) setLoadingResumo(false);
    }
  };

  loadResumo();

  return () => {
    cancelled = true;
  };
}, [token, periodoProcuradoFimParam, periodoProcuradoIniParam, resolvedSpecialState?.kind, checkoutStatusParam]);


// Salvar forma de pagamento quando avançar da etapa proposta_pagamento

useEffect(() => {
  let cancelled = false;

  const loadPagamentoTaxa = async () => {
    if (!token || resolvedSpecialState?.kind === "solicitacao_invalida") {
      setPagamentoTaxaStatus(null);
      setLinkPagamentoTaxaCheckout(null);
      return;
    }

    const result = await buscarPagamentoTaxaPorTokenService(token);

    if (cancelled) return;

    if (!result.ok) {
      const nextSpecialState = getSolicitacaoReservaSpecialState(result.error);

      if (nextSpecialState) {
        setSpecialState(nextSpecialState);
        setStatusSolicitacao(
          nextSpecialState.kind === "pagamento_interrompido"
            ? "PAGAMENTO_TX_INTERROMPIDO"
            : null
        );
      }

      setPagamentoTaxaStatus(null);
      setLinkPagamentoTaxaCheckout(null);
      return;
    }

    setPagamentoTaxaStatus(getPagamentoTaxaCheckoutStatus(result.data));
    setLinkPagamentoTaxaCheckout(getPagamentoTaxaCheckoutLink(result.data));
  };

  loadPagamentoTaxa();

  return () => {
    cancelled = true;
  };
}, [token, checkoutStatusParam, resolvedSpecialState?.kind]);

useEffect(() => {
  const loadHospedes = async () => {
    if (!token) return;

    const resp = await buscarHospedesSolicitacaoReservaPorTokenService(token);

    if (resp?.hospedes) {
      setHospedesInitial(resp);
    }
  };

  loadHospedes();
}, [token]);

const dadosAlterados = (
  values: IdentificacaoValues,
  initial?: Partial<IdentificacaoValues> | null
) => {
  if (!initial) return true;

  return (
    values.nome !== initial.nome ||
    values.sobrenome !== initial.sobrenome ||
    values.celular !== initial.celular ||
    values.email !== initial.email
  );
};

const hospedesAlterados = (newValues: any, initial?: any): boolean => {
  if (!initial?.hospedes || !initial.hospedes.length) {
    // Se não há dados iniciais, considerar como alterado (primeira vez)
    return newValues?.hospedes && newValues.hospedes.length > 0;
  }
  
  const newHospedes = newValues?.hospedes || [];
  
  // Verificar se tamanho mudou
  if (newHospedes.length !== initial.hospedes.length) {
    return true;
  }
  
  // Normalizar data para comparação (extrair apenas YYYY-MM-DD)
  const normalizarData = (data: any): string => {
    if (!data) return "";
    return String(data).slice(0, 10);
  };
  
  // Comparar cada hóspede
  return newHospedes.some((h: any, idx: number) => {
    const ih = initial.hospedes[idx];
    if (!ih) return true;
    return (
      (h.nome ?? "").trim() !== (ih.nome ?? "").trim() ||
      (h.sobrenome ?? "").trim() !== (ih.sobrenome ?? "").trim() ||
      (h.cpf ?? "").replace(/\D/g, "") !== (ih.cpf ?? "").replace(/\D/g, "") ||
      normalizarData(h.dataNascimento) !== normalizarData(ih.dataNascimento) ||
      h.titular !== (ih.titular ?? false)
    );
  });
};

const handleHospedesSubmit = async (payload: any) => {
  
  if (!token) {
    message.error("Token inválido.");
    return;
  }

  try {
    setLoadingHospedes(true);
    
    // 🔍 Verificar se houve alteração
    const houveAlteracao = hospedesAlterados(payload, hospedesInitial);
    
    if (!houveAlteracao) {
      // Não envia PUT se não houve alteração
      next();
      return;
    }
    
    // TODO: implemente no service do front:
    await upsertHospedesSolicitacaoReservaPorTokenService(token, payload);

    next();
  } catch (e: any) {
    console.error(e);
    message.error(e?.message || "Erro ao salvar hóspedes.");
  } finally {
    setLoadingHospedes(false);
  }
};

  const handleIdentificacaoSubmit = async (values: IdentificacaoValues) => {
    try {
      setLoadingIdent(true);

      // SITUAÇÃO 1 (sem token): cria Lead + Postagem + SR e recebe token
      if (!token) {
        const resp = await iniciarSolicitacaoReservaService({
          contatoNome: values.nome,
          contatoSobrenome: values.sobrenome,
          contatoCelular: values.celular,
          contatoEmail: values.email,

          empreendId: context.empreendId,
          dataIni: context.dataIni,
          dataFim: context.dataFim,

          aluguel: context.aluguel,
          troca: context.troca,
          compra: context.compra,
          venda: context.venda,

          precoBaseProprietario: context.precoBaseProprietario,
          valorPropostoClienteInicial: context.valorPropostoClienteInicial,
          validadeDias: context.validadeDias,
        });

        if (!resp?.token) {
          message.error("Não foi possível iniciar a solicitação de reserva.");
          return;
        }

        message.success("Solicitação iniciada. Vamos continuar.");

        const statusAtualizado = await atualizarStatusSolicitacaoReservaPorTokenService(
          resp.token,
          { status: "INICIADA" }
        );

        if (!statusAtualizado) {
          console.warn(
            "[SR] não foi possível atualizar status para INICIADA após criar:",
            resp.token
          );
        }

        // substitui a URL para manter o usuário no mesmo fluxo, agora com token
        navigate(`/solicitacao-reserva/${resp.token}`, { replace: true });

        next();
        return;
      }

      // SITUAÇÃO 2 (com token): por enquanto só avança (depois você fará PATCH contato*)
         if (!solicitacaoReservaId) {
          message.error("Não foi possível identificar a solicitação para atualizar.");
          return;
        }


      // 🔍 verifica se houve alteração
      const houveAlteracao = dadosAlterados(values, initialIdentData);

      if (!houveAlteracao) {
      // Não envia PATCH
        next();
        return;
      }



     const ok = await atualizarContatoSolicitacaoReservaPorTokenService(token, {
      contatoNome: values.nome,
      contatoSobrenome: values.sobrenome,
      contatoCelular: values.celular,
      contatoEmail: values.email,
    });
       if (!ok) {
      message.error("Não foi possível salvar os dados. Tente novamente.");
      return;
    }
    // ✅ mantém o estado local alinhado com o que foi salvo
setInitialIdentData({
  nome: values.nome,
  sobrenome: values.sobrenome,
  celular: values.celular,
  email: values.email,
});
      message.success("Dados confirmados.");
      next();
    } catch (err) {
      console.error(err);
      message.error("Erro ao iniciar a solicitação.");
    } finally {
      setLoadingIdent(false);
    }
  };

  const stepKey = stepDefs[current].key;
  const isStepIdentificacao = stepKey === "identificacao";
  
  const canContinue = useMemo(() => {
  if (stepKey === "proposta_pagamento") return !!formaPagamento;
  return current < stepDefs.length - 1;
  }, [stepKey, formaPagamento, current]);
 const shouldShowContinue =
  stepKey !== "identificacao" &&
  stepKey !== "dados_hospedes" &&
  stepKey !== "taxa_termos_pagamento";

  const handleContinuarPagamento = async () => {
  if (!token) {
    message.error("Token inválido.");
    return;
  }
  if (!formaPagamento) {
    message.warning("Selecione uma forma de pagamento para continuar.");
    return;
  }

  // 🔍 Verificar se houve alteração na forma de pagamento
  if (formaPagamento === formaPagamentoInicial) {
    // Não envia PATCH se não houve alteração
    next();
    return;
  }

  const ok = await salvarFormaPagamentoPorTokenService(token, {
    formaPagamento,
  });

  if (!ok) {
    message.error("Não foi possível salvar a forma de pagamento. Tente novamente.");
    return;
  }

  // Atualizar o estado inicial após salvar com sucesso
  setFormaPagamentoInicial(formaPagamento);
  next();
};

const abrirPagamentoTaxa = async () => {
  if (!token) {
    message.error("Token invÃ¡lido.");
    return false;
  }

  if (loadingPagamentoTaxa) {
    return false;
  }

  try {
    setLoadingPagamentoTaxa(true);

    const result =
      pagamentoTaxaStatus === "VENCIDO" || pagamentoTaxaStatus === "CANCELADO"
        ? await reenviarPagamentoTaxaPorTokenService(token)
        : await buscarPagamentoTaxaPorTokenService(token);

    if (!result.ok) {
      const nextSpecialState = getSolicitacaoReservaSpecialState(result.error);

      if (nextSpecialState) {
        setSpecialState(nextSpecialState);
        setStatusSolicitacao(
          nextSpecialState.kind === "pagamento_interrompido"
            ? "PAGAMENTO_TX_INTERROMPIDO"
            : null
        );
        return false;
      }

      message.error("NÃ£o foi possÃ­vel obter o link de pagamento da taxa.");
      return false;
    }

    const linkPagamento = getPagamentoTaxaCheckoutLink(result.data);
    const statusPagamento = getPagamentoTaxaCheckoutStatus(result.data);

    setPagamentoTaxaStatus(statusPagamento);
    setLinkPagamentoTaxaCheckout(linkPagamento);

    if (!linkPagamento) {
      message.error("Link de pagamento da taxa indisponÃ­vel no momento.");
      return false;
    }

    window.location.href = linkPagamento;
    return true;
  } catch (error) {
    console.error(error);
    message.error("NÃ£o foi possÃ­vel abrir o pagamento da taxa.");
    return false;
  } finally {
    setLoadingPagamentoTaxa(false);
  }
};

const handleTaxaTermosSubmit = async (payload: {
  aceitouTermosUso: boolean;
  aceitouPoliticaPrivacidade: boolean;
  aceitouTermoTaxa: boolean;
}) => {

  if (!token) {
    message.error("Token inválido.");
    return;
  }

  if (loadingTermos) {
    return;
  }

  try {
    setLoadingTermos(true);

    const result = await salvarAceiteTermosPorTokenService(token, payload);

    if (!result.ok) {
      const nextSpecialState = getSolicitacaoReservaSpecialState(result.error);

      if (nextSpecialState) {
        setSpecialState(nextSpecialState);
        setStatusSolicitacao(
          nextSpecialState.kind === "pagamento_interrompido"
            ? "PAGAMENTO_TX_INTERROMPIDO"
            : null
        );
        setLoadingTermos(false);
        return;
      }
      message.error("Não foi possível registrar o aceite dos termos.");
      setLoadingTermos(false);
      return;
    }

    setSpecialState(null);
    setStatusSolicitacao("AGUARDANDO_PAGAMENTO_TAXA");
    setLinkPagamentoTaxaCheckout(getPagamentoTaxaCheckoutLink(result.data));
    setPagamentoTaxaStatus(getPagamentoTaxaCheckoutStatus(result.data));

    message.success("Redirecionando para o checkout da taxa...");

    await abrirPagamentoTaxa();
    setLoadingTermos(false);
    return;

    /*

    // Fluxo legado removido: o link agora vem de GET /pagamento-taxa.

    if (!nextLinkPagamento) {
      message.error("Link de pagamento da taxa não configurado.");
      return;
    }

    setSpecialState(null);
    setStatusSolicitacao("AGUARDANDO_PAGAMENTO_TAXA");

    message.success("Redirecionando para pagamento da taxa...");

    await abrirPagamentoTaxa();

    */
  } catch (error) {
    console.error(error);
    message.error("Não foi possível confirmar os termos.");
    setLoadingTermos(false);
  }
};
const handleIrParaPagamentoTaxa = async () => {
  await abrirPagamentoTaxa();
};
const atualizarStatusSemInteresse = async (
  novoStatus: Extract<
    StatusSolicitacaoReservaFront,
    "SEM_INTERESSE_PERIODO_PROCURADO" | "SEM_INTERESSE_PERIODO_ENCONTRADO"
  >,
  _mensagemSucesso?: string
) => {
  if (!token) {
    message.error("Token inválido.");
    return;
  }

  try {
    setLoadingSemInteresse(true);

    const ok = await atualizarStatusSolicitacaoReservaPorTokenService(token, {
      status: novoStatus,
    });

    if (!ok) {
      message.error("Não foi possível atualizar sua solicitação. Tente novamente.");
      return;
    }

    setStatusSolicitacao(novoStatus);
  } finally {
    setLoadingSemInteresse(false);
  }
};

const handleEncerrarProcura = () => {
  Modal.confirm({
    title: "Encerrar procura?",
    content:
      "Confirma que você não tem mais interesse no período procurado? Essa ação irá encerrar a busca desta solicitação.",
    okText: "Sim, encerrar procura",
    cancelText: "Cancelar",
    okButtonProps: { danger: true },
    onOk: () => atualizarStatusSemInteresse("SEM_INTERESSE_PERIODO_PROCURADO"),
  });
};

const handleContinuarProcurandoOutroPeriodo = () => {
  Modal.confirm({
    title: "Continuar procurando outro período?",
    content:
      "Confirma que você ainda tem interesse no período procurado, mas quer seguir buscando outra opção de datas?",
    okText: "Sim, continuar procurando",
    cancelText: "Cancelar",
    onOk: () =>
      atualizarStatusSemInteresse(
        "SEM_INTERESSE_PERIODO_ENCONTRADO",
        "Sua solicitação foi atualizada para continuar buscando outro período."
      ),
  });
};

  const renderWizardActions = () => (
    <Card style={{ marginTop: 16 }}>
      <Space
        direction={isMobile ? "vertical" : "horizontal"}
        style={{ width: "100%", justifyContent: "space-between" }}
      >
        <Button onClick={back} disabled={current === 0} block={isMobile}>
          Voltar
        </Button>

        {isMobile && (
          <Button onClick={() => setOpenResumo(true)} block>
            Ver resumo
          </Button>
        )}

        {shouldShowContinue && (
          <Button
            type="primary"
            onClick={
              stepDefs[current].key === "proposta_pagamento"
                ? handleContinuarPagamento
                : next
            }
            disabled={!canContinue}
            block={isMobile}
          >
            Continuar
          </Button>
        )}
      </Space>
    </Card>
  );

  const shouldBlockIdent =
  isStepIdentificacao &&
  (loadingStep1 || (!!token && !initialIdentData)); // token mas ainda sem dados

  const wizardContent = (() => {
    switch (stepKey) {
      case "identificacao":
        if (shouldBlockIdent) {
            return <StepSkeletonCard title="Carregando seus dados..." />;
          }
          return (
            <StepIdentificacao
              mode={token ? "confirm" : "collect"}
              loading={loadingStep1}
              initialData={initialIdentData}
              onSubmit={handleIdentificacaoSubmit}
            />
          );

      case "revisao":
  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      <Card
        bordered={false}
        styles={{
          body: {
            padding: 24,
            borderRadius: 16,
          },
        }}
        style={{
          borderRadius: 16,
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        }}
      >
        {/* Header moderno */}
        <Space direction="vertical" size={6} style={{ width: "100%" }}>
         

          <Typography.Title level={2} style={{ margin: 0 }}>
            Revise os detalhes da sua estadia
          </Typography.Title>

          <Typography.Text type="secondary" style={{ fontSize: 16 }}>
            Confirme as informações abaixo antes de avançar para a proposta.
          </Typography.Text>
        </Space>

        <Divider style={{ margin: "16px 0" }} />

        {loadingResumo ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : !resumo ? (
          <Alert
            type="warning"
            showIcon
            message="Resumo indisponível"
            description="Não foi possível carregar os detalhes da estadia. Tente recarregar a página."
          />
        ) : (
          <Space direction="vertical" size={14} style={{ width: "100%" }}>
            {/* Bloco: Resort */}
            <Card
              bordered={false}
              styles={{ body: { padding: 16 } }}
              style={{
                borderRadius: 14,
                background: "rgba(0,0,0,0.02)",
              }}
            >
              <Row gutter={16} align="middle">
                <Col flex="36px">
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(0,0,0,0.06)",
                    }}
                  >
                    <HomeOutlined style={{ fontSize: 18 }} />
                  </div>
                </Col>

                <Col flex="auto">
                  <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                    Resort
                  </Typography.Text>
                  <div style={{ marginTop: 2 }}>
                    <Typography.Title level={3} style={{ margin: 0, lineHeight: 1.2 }}>
                      {resumo.empreendimento.nome}
                    </Typography.Title>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* Bloco: Período */}
            <Card
              bordered={false}
              styles={{ body: { padding: 16 } }}
              style={{
                borderRadius: 14,
                background: "rgba(0,0,0,0.02)",
              }}
            >
              <Row gutter={16} align="middle">
                <Col flex="36px">
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(0,0,0,0.06)",
                    }}
                  >
                    <CalendarOutlined style={{ fontSize: 18 }} />
                  </div>
                </Col>

                <Col flex="auto">
                  <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                    Período da estadia
                  </Typography.Text>

                  <div style={{ marginTop: 2, fontSize: 18, fontWeight: 650 }}>
                    {formatPeriodoResumo(periodoAprovadoIni, periodoAprovadoFim)}
                  </div>

                  <div style={{ marginTop: 8 }}>
                    <Tag style={{ fontSize: 14, padding: "2px 10px", borderRadius: 999 }}>
                      {resolveNoitesAprovadas(resumo) ?? "—"} noites
                    </Tag>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* Bloco: Regras / info */}
            <Alert
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              message={<span style={{ fontSize: 16, fontWeight: 650 }}>Informações importantes</span>}
              description={
                <div style={{ fontSize: 16, lineHeight: 1.65 }}>
                  <Space align="start" size={10}>
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(0,0,0,0.06)",
                        marginTop: 2,
                      }}
                    >
                      <CoffeeOutlined />
                    </div>

                    <div>
                      Café da manhã <b>não incluso</b>, podendo ser adquirido no momento do check-in.
                      <div style={{ marginTop: 6 }}>
                        <Typography.Text type="secondary">
                          Valores e formas de pagamento aparecem na próxima etapa.
                        </Typography.Text>
                      </div>
                    </div>
                  </Space>
                </div>
              }
              style={{
                borderRadius: 14,
                padding: 16,
              }}
            />
          </Space>
        )}
      </Card>
      {renderWizardActions()}
    </div>
  );
   
    case "proposta_pagamento": {
  if (loadingResumo) return <Skeleton active paragraph={{ rows: 10 }} />;

  if (!resumo) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Não foi possível carregar a proposta"
        description="Volte e tente novamente."
      />
    );
  }

  

  const isPix = formaPagamento === "PIX";
  const isCartao = formaPagamento === "CARTAO";
  const exibeAvisoBandeiraParcelamento = resumo.cartao.parcelas > 12;

  const economiaPix = Math.max(
    0,
    resumo.cartao.totalRestanteFinal - resumo.pix.totalRestante
  );

  const cardSelectableStyle = (selected: boolean) => ({
    borderRadius: 16,
    padding: 18,
    cursor: "pointer",
    border: selected ? "2px solid #1677ff" : "1px solid rgba(0,0,0,0.10)",
    background: selected ? "rgba(22,119,255,0.06)" : "white",
    transition: "all .15s ease",
    boxShadow: selected ? "0 6px 18px rgba(22,119,255,0.12)" : "none",
  });
 

  

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Card
        bordered={false}
        style={{ borderRadius: 16, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}
        styles={{ body: { padding: 24 } }}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Typography.Title level={2} style={{ margin: 0 }}>
            Escolha a forma de pagamento
          </Typography.Title>

        

          <Divider style={{ margin: "12px 0" }} />

          <Alert
  type="info"
  showIcon
  style={{ borderRadius: 14 }}
  message={
    <div style={{ fontWeight: 600, fontSize: 16 }}>
      💰 Taxa de Verificação – {moeda(valorTaxa)}
    </div>
  }
  description={
    <div style={{ marginTop: 8 }}>
      <Text>Inicia a busca de disponibilidade junto aos proprietários.</Text>

      <div style={{ marginTop: 10 }}>
        <Text>✔ Devolvida se não houver disponibilidade</Text>
        <br />
        <Text>✔ Abatida do valor final em caso de confirmação</Text>
        <br />
        <Text>
          ✔ Se você desistir após iniciarmos a verificação, a taxa não será devolvida.
        </Text>
      </div>

      <div style={{ marginTop: 10 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Ao pagar, você concorda com as regras acima.
        </Text>
      </div>

      <div style={{ marginTop: 12 }}>
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => setModalOpen(true)}
        >
          Entenda como funciona
        </Button>
      </div>
    </div>
  }
/>

      
        <Alert
          type="success"
          showIcon
          message="O valor abaixo será pago somente após a confirmação da reserva."
          style={{ marginBottom: 16, borderRadius: 12 }}
        />

          {/* CARTÃO EM CIMA */}
          <div onClick={() => setFormaPagamento("CARTAO")} style={cardSelectableStyle(isCartao)}>
            <Row justify="space-between" align="middle" gutter={12}>
              <Col flex="auto">
                <Typography.Text style={{ fontSize: 16, fontWeight: 650 }}>
                  Cartão
                </Typography.Text>
              </Col>

            

              <Col>
                <div style={{ textAlign: "right" }}>
                <Typography.Text style={{ fontSize: 28, fontWeight: 900 }}>
                  Até {resumo.cartao.parcelas}x de {moeda(resumo.cartao.valorParcelaRestante)}
                </Typography.Text>

                <div>
                  <Tag color="green">sem juros</Tag>
                </div>
              </div>
              </Col>
            </Row>

            <div style={{ marginTop: 6 }}>
              <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                Total restante após abatimento da taxa: {moeda(resumo.cartao.totalRestanteFinal)} 
              </Typography.Text>
            </div>

            {exibeAvisoBandeiraParcelamento && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12, borderRadius: 12 }}
                message="Parcelamentos acima de 12x são permitidos somente para cartões Visa e Mastercard."
              />
            )}
          </div>

          {/* PIX EM BAIXO */}
          <div onClick={() => setFormaPagamento("PIX")} style={cardSelectableStyle(isPix)}>
            <Row justify="space-between" align="middle" gutter={12}>
              <Col flex="auto">
                <Typography.Text style={{ fontSize: 16, fontWeight: 650 }}>
                  PIX à vista
                </Typography.Text>
              </Col>

              <Col>
                <Typography.Text style={{ fontSize: 16, fontWeight: 650 }}>
                  Economia no PIX:
                </Typography.Text>
              </Col>

              <Col>
                <Typography.Text style={{ fontSize: 28, fontWeight: 900, lineHeight: 1.05 }}>
                  {moeda(economiaPix)}
                </Typography.Text>
              </Col>
            </Row>

            <div style={{ marginTop: 6 }}>
              <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                Total restante após abatimento da taxa: {moeda(resumo.pix.totalRestante)}
              </Typography.Text>
            </div>
          </div>

          {!formaPagamento && (
            <Alert
              type="warning"
              showIcon
              message="Selecione uma forma de pagamento para continuar."
              style={{ borderRadius: 14 }}
            />
          )}
        </Space>
      </Card>
    </div>
  );
}


      case "processo_prazos":
  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Card
        bordered={false}
        style={{ borderRadius: 16, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}
        styles={{ body: { padding: 24 } }}
      >
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          
          <Typography.Title level={2} style={{ margin: 0 }}>
            Processo e prazos
          </Typography.Title>

          {/* 📝 Revisão */}
          <Card
            bordered={false}
            style={{
              borderRadius: 14,
              background: "rgba(0,0,0,0.02)",
            }}
            styles={{ body: { padding: 18 } }}
          >
            <Space direction="vertical" size={8}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                📝 Revise antes de enviar
              </Typography.Title>

              <Typography.Text>
                Por favor, revise as informações antes de enviar sua solicitação de reserva.
              </Typography.Text>

              <Typography.Text>
                Após o pagamento da taxa, iniciaremos imediatamente o processo de verificação junto aos proprietários compatíveis.
              </Typography.Text>
            </Space>
          </Card>

          {/* 💰 Taxa */}
          <Card
            bordered={false}
            style={{
              borderRadius: 14,
              background: "rgba(0,0,0,0.02)",
            }}
            styles={{ body: { padding: 18 } }}
          >
            <Space direction="vertical" size={10}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                💰 Sobre a Taxa de Verificação
              </Typography.Title>

              <Typography.Text>
                Será cobrada uma <strong>Taxa de Verificação</strong> no valor de{" "}
                <strong>{moeda(valorTaxa)}</strong>.
              </Typography.Text>

              <ul style={{ paddingLeft: 18, margin: 0 }}>
                <li>Inicia a busca e consulta de disponibilidade junto aos proprietários;</li>
                <li>Prioriza sua solicitação dentro do prazo informado;</li>
                <li>
                  Será integralmente abatida do valor total da reserva em caso
                  de confirmação e pagamento do saldo restante.
                </li>
              </ul>

              <Alert
                type="warning"
                showIcon
                message="Importante"
                description="A Taxa de Verificação não será devolvida após o início do processo, exceto caso não seja possível confirmar a reserva dentro do prazo informado."
                style={{ borderRadius: 12 }}
              />
            </Space>
          </Card>

          {/* ⏳ Prazo */}
          <Card
            bordered={false}
            style={{
              borderRadius: 14,
              background: "rgba(0,0,0,0.02)",
            }}
            styles={{ body: { padding: 18 } }}
          >
            <Space direction="vertical" size={10}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                ⏳ Prazo do Processo
              </Typography.Title>

              <Typography.Text>
                O prazo para confirmação da reserva é de até <strong>7 dias úteis</strong>.
              </Typography.Text>

              <Typography.Text>
                Assim que houver confirmação da reserva pelo empreendimento:
              </Typography.Text>

              <ul style={{ paddingLeft: 18, margin: 0 }}>
                <li>Você será notificado imediatamente;</li>
                <li>
                  Terá o prazo de <strong>3 dias corridos</strong> para realizar o pagamento do valor restante da hospedagem.
                </li>
              </ul>
            </Space>
          </Card>

          {/* 💳 Pagamento */}
          <Card
            bordered={false}
            style={{
              borderRadius: 14,
              background: "rgba(0,0,0,0.02)",
            }}
            styles={{ body: { padding: 18 } }}
          >
            <Space direction="vertical" size={10}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                💳 Pagamento do Valor Restante
              </Typography.Title>

              <Typography.Text>
                O pagamento do saldo restante será solicitado somente após a confirmação da reserva.
              </Typography.Text>

              <ul style={{ paddingLeft: 18, margin: 0 }}>
                <li>
                  Caso o pagamento não seja realizado dentro do prazo informado{" "}
                  <strong>(3 dias corridos)</strong>:
                  <ul style={{ paddingLeft: 18, marginTop: 6, marginBottom: 0 }}>
                    <li>A reserva será cancelada;</li>
                    <li>O proprietário será liberado;</li>
                    <li>A Taxa de Verificação <strong>não será devolvida</strong>.</li>
                  </ul>
                </li>
                <li>A quantidade de parcelas e a forma de pagamento (PIX ou cartão) poderá ser alterada no momento do pagamento.</li>
              </ul>
            </Space>
          </Card>

        </Space>
      </Card>
    </div>
  );

      case "dados_hospedes":
        return (
          <StepHospedes
            initial={hospedesInitial}
            loading={loadingHospedes}
            onSubmit={handleHospedesSubmit}
          />
        );

          

     case "taxa_termos_pagamento":
  return (
    <StepTaxaTermosPagamento
      loading={loadingTermos}
      taxa={valorTaxa}
      prazoBuscaDiasUteis={7}
      prazoPagamentoRestanteDias={3}
      initialValues={{
        aceitouTermosUso: false,
        aceitouPoliticaPrivacidade: false,
        aceitouTermoTaxa: false,
      }}
      onSubmit={handleTaxaTermosSubmit}
    />
  );

      default:
        return null;
    }
  })();

  const mainContent = (() => {
  if (resolvedSpecialState) {
    return (
      <StepEstadoSolicitacao
        variant={resolvedSpecialState.kind}
        message={resolvedSpecialState.message}
        resumo={resumo}
        periodoProcuradoIni={periodoProcuradoIni}
        periodoProcuradoFim={periodoProcuradoFim}
        periodoEncontradoIni={periodoAprovadoIni}
        periodoEncontradoFim={periodoAprovadoFim}
      />
    );
  }

  if (isSemInteresseMode) {
    if (loadingToken || loadingResumo) {
      return <Skeleton active paragraph={{ rows: 8 }} />;
    }

    if (isSemInteresseConcluido && statusSolicitacao) {
      return (
        <StepAgradecimentoSemInteresse
          status={
            statusSolicitacao as Extract<
              StatusSolicitacaoReservaFront,
              "SEM_INTERESSE_PERIODO_PROCURADO" | "SEM_INTERESSE_PERIODO_ENCONTRADO"
            >
          }
          empreendimento={resumo?.empreendimento?.nome}
          periodoProcuradoIni={periodoProcuradoIni}
          periodoProcuradoFim={periodoProcuradoFim}
          onBuscarOutroPeriodo={() => navigate("/queroviajar")}
        />
      );
    }

    return (
      <StepSemInteressePeriodo
        empreendimento={resumo?.empreendimento?.nome}
        periodoProcuradoIni={periodoProcuradoIni}
        periodoProcuradoFim={periodoProcuradoFim}
        periodoEncontradoIni={periodoAprovadoIni}
        periodoEncontradoFim={periodoAprovadoFim}
        loading={loadingSemInteresse}
        onEncerrarProcura={handleEncerrarProcura}
        onContinuarProcurandoOutroPeriodo={handleContinuarProcurandoOutroPeriodo}
      />
    );
  }

  if (isAguardandoPagamentoTaxa) {
    return (
      <StepAguardandoPagamentoTaxa
        resumo={resumo}
        statusSolicitacao={statusSolicitacao}
        loadingPagamento={loadingPagamentoTaxa}
        pagamentoStatus={pagamentoTaxaStatus}
        checkoutRetornoStatus={checkoutStatusParam}
        checkoutDisponivel={!!linkPagamentoTaxaCheckout}
        onIrParaPagamento={handleIrParaPagamentoTaxa}
      />
    );
  }

  if (isAcompanhamentoTaxaPaga) {
    return (
      <StepTaxaPagaAcompanhamento
        resumo={resumo}
        periodoEncontradoIni={periodoAprovadoIni}
        periodoEncontradoFim={periodoAprovadoFim}
        statusSolicitacao={statusSolicitacao}
        pagamentoTaxaEm={pagamentoTaxaEm}
        prazoFinalBusca={prazoFinalBusca}
        pagamentoStatus={pagamentoTaxaStatus}
      />
    );
  }

  if (isAcompanhamentoPosTaxa) {
    return (
      <StepAcompanhamentoSolicitacao
        resumo={resumo}
        periodoEncontradoIni={periodoAprovadoIni}
        periodoEncontradoFim={periodoAprovadoFim}
        formaPagamento={formaPagamento}
        statusSolicitacao={statusSolicitacao}
      />
    );
  }

  return wizardContent;
})();

  function StepSkeletonCard({ title }: { title?: string }) {
  return (
    <Card>
      {title && (
        <Typography.Title level={4} style={{ marginBottom: 16 }}>
          {title}
        </Typography.Title>
      )}
      <Skeleton active paragraph={{ rows: 6 }} />
    </Card>
  );
}

  return (
    <>
     <div ref={topAnchorRef} />
     <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="fechar" type="primary" onClick={() => setModalOpen(false)}>
            Entendi
          </Button>,
        ]}
        title="Taxa de Verificação e Prioridade da Solicitação"
      >
        <Typography>
          <Paragraph>
            Para dar início ao processo de busca e intermediação da reserva,
            é cobrada uma <strong>Taxa de Verificação</strong> no valor de{" "}
            <strong>{moeda(valorTaxa)}</strong>.
          </Paragraph>

          <Paragraph>
            Essa taxa tem como finalidade:
          </Paragraph>

          <ul>
            <li>Acionar o sistema de busca de proprietários compatíveis;</li>
            <li>Realizar contato e consulta de disponibilidade;</li>
            <li>Intermediar negociações de valor, quando necessário;</li>
            <li>
              Priorizar operacionalmente a sua solicitação dentro do prazo
              informado.
            </li>
          </ul>

          <Title level={5}>Regras da Taxa</Title>

          <Paragraph>
            Caso não seja encontrado proprietário interessado ou disponível
            dentro do prazo informado, o valor é devolvido integralmente ao
            interessado.
          </Paragraph>

          <Paragraph>
            Caso o interessado desista da solicitação após o pagamento da
            taxa, o valor não será restituído, uma vez que os procedimentos
            de verificação e intermediação já terão sido iniciados.
          </Paragraph>

          <Paragraph>
            Em caso de confirmação da reserva, o valor da Taxa de Verificação
            será integralmente abatido do valor total da hospedagem, não
            representando custo adicional.
          </Paragraph>

          <Paragraph>
            O pagamento da taxa implica concordância com essas condições.
          </Paragraph>
        </Typography>
      </Modal>
    <div style={{ padding: isMobile ? 12 : 24, maxWidth: 1200, margin: "0 auto" }}>
      {/* Header do wizard */}
      {isWizardMode && !isSemInteresseMode && !resolvedSpecialState && (
        <div style={{ marginBottom: 16 }}>
          {isMobile ? (
            <>
              <Text type="secondary">
                Etapa {current + 1} de {stepDefs.length} — {stepDefs[current].title}
              </Text>
              <div style={{ marginTop: 8 }}>
                <Progress percent={pct} />
              </div>
            </>
          ) : (
            <Steps current={current} items={stepItems} />
          )}
        </div>)}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={isSemInteresseMode || resolvedSpecialState ? 24 : 16}>
          {mainContent}

          {/* Ações */}
          {isWizardMode &&
            !isSemInteresseMode &&
            !resolvedSpecialState &&
            stepKey !== "revisao" && (
            renderWizardActions()
          )}
        </Col>

        {/* Resumo Desktop */}
        {!isMobile && !isSemInteresseMode && !resolvedSpecialState && (
          <Col xs={24} md={8}>
            <ResumoCard
             currentStep={current}
             currentStepKey={stepKey}
             resumo={resumo}
             periodoAprovadoIni={periodoAprovadoIni}
             periodoAprovadoFim={periodoAprovadoFim}
             formaPagamento={formaPagamento}
              statusSolicitacao={statusSolicitacao}
              forceShowPrice={!isWizardMode}
          />
          </Col>
        )}
      </Row>

      {/* Resumo Mobile */}
      {!isSemInteresseMode && !resolvedSpecialState && (
        <Drawer
          title="Resumo da proposta"
          open={openResumo}
          onClose={() => setOpenResumo(false)}
          placement="bottom"
          height="70%"
        >
          <ResumoCard
            currentStep={current}
            currentStepKey={stepKey}
            resumo={resumo}
            periodoAprovadoIni={periodoAprovadoIni}
            periodoAprovadoFim={periodoAprovadoFim}
            formaPagamento={formaPagamento}
            statusSolicitacao={statusSolicitacao}
            forceShowPrice={!isWizardMode}
          />
        </Drawer>
      )}
    </div>
    </>
  );
}
